<template>
  <FormGroup>
    <RadioButtonGroup>
      <RadioButton v-model="form.fields.hasParking" name="hasParking" :value="true">Has parking</RadioButton>
      <RadioButton v-model="form.fields.hasParking" name="hasParking" :value="false">No parking</RadioButton>
    </RadioButtonGroup>

    <FormGroup v-if="form.fields.hasParking">
      <Divider variant="primary" />

      <FormField label="Parking type" :error="form.fieldErrors('parkingType')">
        <Select v-model="form.fields.parkingType">
          <option v-for="option in parkingTypeOptions" :key="option" :value="option">{{ option }}</option>
        </Select>
      </FormField>

      <FormField label="Parking monthly fee per space" :error="form.fieldErrors('parkingMonthlyChargePerSpace')">
        <Input v-model="form.fields.parkingMonthlyChargePerSpace" type="number">
          <template #prefix>$</template>
        </Input>
      </FormField>

      <FormField label="Number of spaces" direction="row" :error="form.fieldErrors('parkingAvailableSpaces')">
        <InputStepper v-model="form.fields.parkingAvailableSpaces" name="parkingAvailableSpaces" />
      </FormField>

      <FormField
        label="Parking spaces are tandem"
        direction="row"
        label-position="after"
        :error="form.fieldErrors('parkingSpacesAreTandem')"
      >
        <Checkbox v-model="form.fields.parkingSpacesAreTandem" name="parkingSpacesAreTandem" />
      </FormField>

      <FormField label="Other description" :error="form.fieldErrors('parkingOtherDescription')">
        <Textarea v-model="form.fields.parkingOtherDescription" rows="3" />
      </FormField>
    </FormGroup>
  </FormGroup>
</template>

<script setup lang="ts">
import { watch } from 'vue';

import {
  Checkbox,
  RadioButton,
  InputStepper,
  Input,
  FormField,
  FormGroup,
  Select,
  Textarea,
  RadioButtonGroup,
  Divider,
  useForm,
} from '@tallo/design-system';
import { ParkingDto, ParkingType } from '@tallo/property';

import { SaveParkingDto } from '../../dtos/save-parking.dto.interface';
import { usePropertyStore } from '../../stores/property.store';

const parkingTypeOptions = Object.values(ParkingType);
const propertyStore = usePropertyStore();
const form = useForm<SaveParkingDto>({
  hasParking: true,
  parkingType: null,
  parkingAvailableSpaces: 1,
  parkingSpacesAreTandem: null,
  parkingOtherDescription: '',
  parkingMonthlyChargePerSpace: null,
});

async function save() {
  return form.submit(async (fields) => await propertyStore.saveParking(fields));
}

function populateForm(parking: ParkingDto) {
  Object.assign(form.fields, {
    hasParking: parking?.hasParking ?? true,
    parkingType: parking?.parkingType || null,
    parkingAvailableSpaces: parking?.parkingAvailableSpaces ?? 1,
    parkingSpacesAreTandem: parking?.parkingSpacesAreTandem ?? null,
    parkingOtherDescription: parking?.parkingOtherDescription || '',
    parkingMonthlyChargePerSpace: parking?.parkingMonthlyChargePerSpace ?? null,
  });
}

watch(() => propertyStore.property?.parking!, populateForm, { immediate: true });

defineExpose({ save, form });
</script>
